import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '~/components/buttons/NewButton';
import AlertMessage from '~/components/data-display/AlertMessage';
import { HStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { useAnalytics } from '~/hooks/useAnalytics';
import { RouteConfig } from '~/routes/routeConfig';
import { AnalyticsEvents } from '~/services/analytics';
import { UserIdentificationStatus } from '~/types/user';
import { UserBankAccount, UserBankAccountBindingError, UserBankAccountStatus } from '~/types/user-bank-account';
import styles from './UserBankAccountOnboardingAlert.module.scss';

interface AlertContent {
  titleKey: 'verify_email.title' | 'verify_identity.title';
  buttonKey: 'verify_email.button' | 'verify_identity.button';
}

interface Props {
  userBankAccount: UserBankAccount;
}

export const UserBankAccountOnboardingAlert = ({ userBankAccount }: Props) => {
  const { t } = useTranslation('accounts', { keyPrefix: 'user_bank_account.onboarding' });
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const bindingErrors: UserBankAccountBindingError[] = userBankAccount.bindingErrors || [];

  const needsEmailVerification =
    bindingErrors.includes(UserBankAccountBindingError.EMAIL_VERIFIED_MATCH_ERROR) ||
    userBankAccount.status === UserBankAccountStatus.INVITATION_SENT;

  const needsIdentityVerification = bindingErrors.includes(UserBankAccountBindingError.ID_VERIFIED_MATCH_ERROR);
  const isIdentificationPending =
    needsIdentityVerification && userBankAccount.user?.identificationStatus?.includes(UserIdentificationStatus.PENDING);

  const getAlertContent = (): AlertContent => {
    if (needsEmailVerification) {
      return { titleKey: 'verify_email.title', buttonKey: 'verify_email.button' };
    }
    return { titleKey: 'verify_identity.title', buttonKey: 'verify_identity.button' };
  };

  const handleOnClick = () => {
    track(AnalyticsEvents.USER_BANK_ACCOUNT_BANNER_CLICKED, {
      status: userBankAccount.status,
      bindingErrors,
    });
    navigate(RouteConfig.UserBankAccountsOnboarding.buildLink());
  };

  const content = getAlertContent();

  return (
    <div className={styles.container}>
      <AlertMessage box type={isIdentificationPending ? 'info' : 'warning'} className={styles.alert} size="medium">
        {isIdentificationPending ? (
          t('user_identification_pending')
        ) : (
          <HStack gap={2} justify="space-between" align="center" className={styles.alertContent}>
            <Text variant="body2" weight="regular">
              {t(content.titleKey)}
            </Text>
            <Button onClick={handleOnClick}>{t(content.buttonKey)}</Button>
          </HStack>
        )}
      </AlertMessage>
    </div>
  );
};
